// React
import { memo, useCallback, useEffect, useState } from "react";

// Servisni sloj
import { getCurrenciesAPI } from "services/api/lookups/api";
import { getPaymentMethodsAPI } from "services/api/finance/lookups/api";
import { getContactAPI } from "services/api/contacts/api";
import {
  useHandleDateChange,
  useHandleDropdownChange,
  useHandleNumberFieldChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";

// UI
import { Button } from "ui/components/Button/Button";
import { DatePicker } from "ui/components/DatePicker";
import ContactDropdown from "ui/components/Dropdown/ContactDropdown/ContactDropdown";
import { Dropdown } from "ui/components/Dropdown/Dropdown";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import { TextField } from "ui/components/TextField/TextField";
import { NumberField } from "ui/components/TextField/NumberField";
import { IbanTextField } from "ui/components/TextField/IbanTextField";
import ContactEditCard from "ui/modules/contacts/pages/ContactDetailPage/components/ContactDetailCard/components/ContactEditCard/ContactEditCard";
import InvoiceIncomingSupplierTooltip from "ui/modules/finance/pages/invoices/InvoiceSearchPage/components/InvoiceIncomingPreviewCard/components/InvoiceIncomingInfo/components/InvoiceIncomingHeader/components/InvoiceIncomingSupplierTooltip";

import { FieldConfidence } from "./components/FieldConfidence";
import { buildContactAddress } from "./transformations";
import { DEFAULT_CURRENCY_ID } from "./consts";
import { InvoiceIncomingEditInfoProps } from "./types";

/**
 * Zaglavlje ulaznog računa — isti raspored kao kartica B2B računa, da isti račun izgleda jednako
 * bez obzira dolazi li kroz providera, iz datoteke ili se upisuje ručno. Dobavljač se bira samo
 * kroz kontakte: naziv, OIB i adresa dolaze s kontakta, pa ne postoje dva zapisa istog dobavljača.
 */
const InvoiceIncomingEditInfo: React.FC<InvoiceIncomingEditInfoProps> = (props) => {
  const { formValues, setFormValues, errors, fieldConfidences, onFieldFocus } = props;

  const [currencyOptions, setCurrencyOptions] = useState<DropdownOption[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<DropdownOption[]>([]);
  const [showContactModal, setShowContactModal] = useState<boolean>(false);

  const handleTextFieldChange = useHandleTextFieldChange(setFormValues, formValues);
  const handleDropdownChange = useHandleDropdownChange(setFormValues, formValues);
  const handleDateChange = useHandleDateChange(setFormValues, formValues);
  const handleNumberFieldChange = useHandleNumberFieldChange(setFormValues, formValues);

  useEffect(() => {
    fetchLookups();
  }, []);

  const fetchLookups = async () => {
    try {
      const [currencies, paymentMethods] = await Promise.all([
        getCurrenciesAPI(),
        getPaymentMethodsAPI(),
      ]);

      setCurrencyOptions(currencies?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
      setPaymentMethodOptions(paymentMethods?.map((item: any) => ({ text: item.name, value: item.id })) ?? []);
    } catch {
      // greška se prikazuje kroz snackbar
    }
  };

  /**
   * Uz odabir kontakta na račun se prepisuju naziv, OIB i adresa. Poslužitelj ih sprema uz račun
   * kao zapis onoga tko ga je izdao, pa moraju biti popunjeni iako im na ekranu nema polja.
   */
  const applyContact = useCallback(async (contactId: string) => {
    try {
      const contact = await getContactAPI(contactId);

      setFormValues(previous => ({
        ...previous,
        contactSupplierId: contactId,
        supplierName: contact.name ?? contact.companyName ?? previous.supplierName,
        supplierOIB: contact.personalIdentificationNumber ?? previous.supplierOIB,
        supplierAddress: buildContactAddress(contact.primaryContactAddress) ?? previous.supplierAddress,
      }));
    } catch {
      // greška se prikazuje kroz snackbar; veza je svejedno postavljena
      setFormValues(previous => ({ ...previous, contactSupplierId: contactId }));
    }
  }, [setFormValues]);

  const handleSupplierChange = useCallback((_event: any, _name: string, item: DropdownOption) => {
    if (!item?.value) {
      setFormValues(previous => ({ ...previous, contactSupplierId: null }));
      return;
    }

    applyContact(item.value);
  }, [applyContact, setFormValues]);

  const handleContactModalOpen = useCallback(() => {
    setShowContactModal(true);
  }, []);

  const handleContactModalClose = useCallback(() => {
    setShowContactModal(false);
  }, []);

  const handleContactCreated = useCallback((contactId?: string) => {
    setShowContactModal(false);

    if (contactId) applyContact(contactId);
  }, [applyContact]);

  // Tečaj ima smisla samo kad račun nije u eurima.
  const isForeignCurrency = !!formValues.currencyId
    && formValues.currencyId.toLowerCase() !== DEFAULT_CURRENCY_ID;

  // Pročitani dobavljač bez kontakta — iz tih se podataka stvara novi kontakt.
  const hasReadSupplier = !!formValues.supplierName || !!formValues.supplierOIB;
  const showCreateContact = !formValues.contactSupplierId && hasReadSupplier;

  const readSupplierDetails = [
    formValues.supplierName,
    formValues.supplierOIB ? `OIB ${formValues.supplierOIB}` : null,
  ].filter(Boolean);

  return (
    <div className="flex flex-col gap-3">
      <div className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Račun</div>

      {/* Polja vraćaju labelu i sam unos kao dva elementa, pa svako mora imati vlastitu ćeliju —
          bez omota bi labela i unos završili u različitim stupcima. */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
        <FieldConfidence
          fieldKey="supplier_name"
          confidence={fieldConfidences?.supplier_name}
          onFieldFocus={onFieldFocus}
        >
          <ContactDropdown
            name="contactSupplierId"
            label="Dobavljač"
            value={formValues.contactSupplierId}
            onChange={handleSupplierChange}
            error={errors.contactSupplierId}
            required
            tooltip={
              <InvoiceIncomingSupplierTooltip
                name={formValues.supplierName}
                address={formValues.supplierAddress}
                oib={formValues.supplierOIB}
                vat={formValues.supplierVAT}
                iban={formValues.supplierIBAN}
              />
            }
          />
        </FieldConfidence>

        <FieldConfidence
          fieldKey="invoice_number"
          confidence={fieldConfidences?.invoice_number}
          onFieldFocus={onFieldFocus}
        >
          <TextField
            type="text"
            name="invoiceNumber"
            label="Broj računa"
            value={formValues.invoiceNumber}
            onChange={handleTextFieldChange}
            error={errors.invoiceNumber}
            required
          />
        </FieldConfidence>

        <FieldConfidence
          fieldKey="invoice_date"
          confidence={fieldConfidences?.invoice_date}
          onFieldFocus={onFieldFocus}
        >
          <DatePicker
            name="invoiceDate"
            label="Datum računa"
            value={formValues.invoiceDate}
            onChange={handleDateChange}
            error={errors.invoiceDate}
            required
          />
        </FieldConfidence>

        <FieldConfidence
          fieldKey="due_date"
          confidence={fieldConfidences?.due_date}
          onFieldFocus={onFieldFocus}
        >
          <DatePicker
            name="dueDate"
            label="Datum dospijeća"
            value={formValues.dueDate}
            onChange={handleDateChange}
          />
        </FieldConfidence>

        <div>
          <DatePicker
            name="deliveryDate"
            label="Datum isporuke"
            value={formValues.deliveryDate}
            onChange={handleDateChange}
          />
        </div>

        <div>
          <Dropdown
            name="currencyId"
            label="Valuta"
            placeholder="Odaberite valutu"
            options={currencyOptions}
            value={formValues.currencyId}
            onChange={handleDropdownChange}
          />
        </div>

        {/* PDV ID i IBAN ostaju na računu jer ih kontakt nema, a čitaju se sa samog dokumenta. */}
        <div>
          <TextField
            type="text"
            name="supplierVAT"
            label="PDV ID"
            value={formValues.supplierVAT}
            onChange={handleTextFieldChange}
          />
        </div>

        {isForeignCurrency && (
          <div>
            <NumberField
              name="exchangeRate"
              label="Tečaj"
              value={formValues.exchangeRate}
              onChange={handleNumberFieldChange}
              decimals={4}
            />
          </div>
        )}
      </div>

      {showCreateContact && (
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>Dobavljač s računa: {readSupplierDetails.join(" · ")}</span>
          <Button variant="text" children="Kreiraj iz računa" onClick={handleContactModalOpen} />
        </div>
      )}

      {/* Linija se razvlači do okomitih obruba okvira, pa `-mx-4 px-4` poništava padding taba. */}
      <div className="border-t border-slate-200 pt-4 mt-1 -mx-4 px-4 text-xs uppercase tracking-wide text-slate-400 font-semibold">
        Plaćanje
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-x-4 gap-y-3">
        <div>
          <Dropdown
            name="paymentMethodId"
            label="Način plaćanja"
            placeholder="Odaberite način plaćanja"
            options={paymentMethodOptions}
            value={formValues.paymentMethodId}
            onChange={handleDropdownChange}
          />
        </div>

        <div>
          <TextField
            type="text"
            name="paymentReference"
            label="Poziv na broj"
            value={formValues.paymentReference}
            onChange={handleTextFieldChange}
          />
        </div>

        <div>
          <NumberField
            name="durationDays"
            label="Rok plaćanja (dana)"
            value={formValues.durationDays}
            onChange={handleNumberFieldChange}
            decimals={0}
          />
        </div>

        <div>
          <IbanTextField
            type="text"
            name="supplierIBAN"
            label="IBAN dobavljača"
            value={formValues.supplierIBAN}
            onChange={handleTextFieldChange}
            showBankName
          />
        </div>
      </div>

      {/* Unos kontakta popunjen onim što je pročitano s računa — isti obrazac koji nudi i „+"
          na samom dropdownu. */}
      <ContactEditCard
        contactId={null}
        show={showContactModal}
        onSuccess={handleContactCreated}
        onClose={handleContactModalClose}
        isFullScreen={false}
        initialContactTypeId="C"
        initialName={formValues.supplierName}
        initialOib={formValues.supplierOIB}
      />
    </div>
  );
};

export default memo(InvoiceIncomingEditInfo);
